import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/__common/Modal/Modal";
import Accordion from "../../../components/__common/Accordion/Accordion";
import FocusTrapping from "../../../components/__hoc/FocusTrapping";
import GoogleAnalytics from "../../../utils/GoogleAnalytics";
import { UtilsKeyboardAccessibility } from "../../../utils/UtilsKeyboardAccessibility";
import {
  EVENT_CATEGORY_GPS,
  EVENT_GPS_ACTION_LIST,
} from "../../../constants/analytics";
import { GpsRegions } from "../../../data/gps-regions";
import { GpsCountry, GpsRegion } from "../../../types/gps-region";
import { GpsCountryList } from "./GpsCountryList";

interface GpsListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GpsListModal: React.FC<GpsListModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedModalRegion, setSelectedModalRegion] = useState<
    GpsRegion | undefined
  >();

  // Pre-compute last-region and last-country outside any .map()
  const lastRegion = GpsRegions.at(-1);

  const handleCountrySelect = (country: GpsCountry) => {
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_GPS,
      `${EVENT_GPS_ACTION_LIST}-${country.code}`
    );
    window.open(country.link, "_blank");
  };

  // Focus management when modal opens
  useEffect(() => {
    if (isOpen) {
      UtilsKeyboardAccessibility.setFocus("global-rights-modal");
    }
  }, [isOpen]);

  // Tab-trap wiring for accordion items
  useEffect(() => {
    if (!isOpen) return;

    const isLastRegion = selectedModalRegion?.name === lastRegion?.name;

    if (isLastRegion) {
      UtilsKeyboardAccessibility.addTabEventListener(
        "last-language",
        "global-rights-modal"
      );
    } else {
      UtilsKeyboardAccessibility.addTabEventListener(
        "last-region",
        "global-rights-modal"
      );
    }

    return () => {
      UtilsKeyboardAccessibility.removeTabEventListener(
        "last-language",
        "global-rights-modal"
      );
      UtilsKeyboardAccessibility.removeTabEventListener(
        "last-region",
        "global-rights-modal"
      );
    };
  }, [selectedModalRegion, isOpen, lastRegion]);

  return (
    <FocusTrapping
      isActive={isOpen}
      modalId="global-rights-modal"
      onHandleClose={onClose}
    >
      <Modal
        className="global-rights__modal"
        show={isOpen}
        id="global-rights-modal"
      >
        <Modal.Header onHide={onClose}>
          <Modal.Title>{t("PRIVACY_GRRS_SELECT_COUNTRY")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Accordion>
            {GpsRegions.map((region, index) => (
              <Accordion.Item
                id={
                  index === GpsRegions.length - 1 ? "last-region" : region.code
                }
                testId="region-selected-btn"
                title={region.name}
                key={region.code}
                activeItem={selectedModalRegion?.name}
                onClick={(isActive) => {
                  setSelectedModalRegion(isActive ? region : undefined);
                }}
              >
                <GpsCountryList
                  countries={region.countries}
                  lastItemId={
                    region.name === lastRegion?.name
                      ? "last-language"
                      : undefined
                  }
                  onCountrySelect={handleCountrySelect}
                />
              </Accordion.Item>
            ))}
          </Accordion>
        </Modal.Body>
      </Modal>
    </FocusTrapping>
  );
};
